import {
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from "@mui/material";
import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import { useNavigate } from "react-router";
import { MILLIS_TO_LOCALE } from "../../../utilities/helpers/formatUnix";
import { ERROR_HEADERS_CSV, INGESTION_STATUS } from "../LandingPage/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import GeneralInfoModal from "../../UI/atoms/Popup/GeneralInfoModal";
import { CSVLink } from "react-csv";

import VisibilityIcon from "@mui/icons-material/Visibility";

dayjs.extend(utc);

const ValidationLogsTable = forwardRef(({ handleLoader }: any, ref: any) => {
    // hooks
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [ingestionList, setIngestionLogsList] = useState<any>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [skipKey, setSkipKey] = useState("");
    const [skipKeyQueue, setskipKeyQueue] = useState<any>("");
    const [validationErrors, setvalidationErrors] = useState<any>(null);
    const [selectedFile, setSelectedFile] = useState<any>(null);
    const [isValidatePopupOpen, setIsValidatePopupOpen] = useState(false);
    const [filterOptions, setFilterOptions] = useState<any>({
        status: [...INGESTION_STATUS.map((item) => item.key)],
        completedAfter: null,
        completedBefore: null,
    });

    const onFilterOptionsChange = (filter: any) => {
        console.log(filter);

        if (filter.hasOwnProperty("status")) {
            if (filterOptions.status.includes(filter.status)) {
                filter.status = filterOptions.status.filter(
                    (status: string) => status != filter.status
                );
            } else {
                filter.status = [...filterOptions.status, filter.status];
            }
        }
        const updatedFilter = { ...filterOptions, ...filter };
        setFilterOptions(updatedFilter);
        getIngestionLogsList({
            ...updatedFilter,
            status: updatedFilter.status,
        });
    };

    useImperativeHandle(
        ref,
        () => ({
            getIngestionLogsList,
        }),
        []
    );

    useEffect(() => {
        getIngestionLogsList();
    }, []);

    const resetTableMeta = () => {
        setskipKeyQueue([]);
        setPage(0);
    };

    const getIngestionLogsList = async (filters = filterOptions) => {
        resetTableMeta();
        const pages = await getIngestionLogs(
            rowsPerPage,
            undefined,
            undefined,
            filters
        );
        if (pages) {
            setTotalCount(pages.totalCount);
            setIngestionLogsList([...pages.list]);
        }
    };

    const ingestionValidatePopupOpen = (row: any) => {
        setIsValidatePopupOpen(true);
        setvalidationErrors(row);
    };

    const getIngestionLogs = async (
        limit: number | string = rowsPerPage || 0,
        skip: string = skipKey || "",
        skipQueue: any[] | null = skipKeyQueue,
        filters = filterOptions
    ) => {
        const logs = await getLogsAPI(limit, skip, filters);
        if (logs) {
            const list = logs?.list;
            list.length &&
                pushKey(
                    `${list[list.length - 1]?.id}__${
                        list[list.length - 1]?.lastModified
                    }`,
                    skipQueue
                );
            return logs;
        }
        return null;
    };

    const getLogsAPI = async (
        limit: number | string,
        skip: string = "",
        filters: any = null
    ) => {
        try {
            if (filters?.status.length == 0)
                filters.status = [...INGESTION_STATUS.map((item) => item.key)];
            handleLoader(true);
            const url = `${
                process.env.BASE_URL
            }api/v1/cms/ingest/all?logType=VALIDATION_LOG&limit=${limit}&skip=${skip}&status=${
                filters?.status.join(",") || ""
            }&completedAfter=${filters?.completedAfter || ""}&completedBefore=${
                filters?.completedBefore || ""
            }`;
            const saveVersion = await axios.get(url);
            return saveVersion.data?.data;
        } catch (error: any) {
            console.log("Error in getting master information", error.response);
            if (error.response?.data?.message)
                [
                    enqueueSnackbar(error.response?.data?.message, {
                        variant: "warning",
                    }),
                ];
            else {
                enqueueSnackbar(
                    "API failed with error : " + error.response?.statusText,
                    { variant: "warning" }
                );
            }
            return null;
        } finally {
            handleLoader(false);
        }
    };

    const tableRowRender = () => {
        return (
            <>
                <TableRow sx={{ height: 20 }}>
                    <TableCell className="border-none pt-0 pd-0" align="right">
                        <h3>#</h3>
                    </TableCell>
                    <TableCell className="border-none pt-0 pd-0">
                        <h3>Metadata Spreadsheet</h3>
                    </TableCell>
                    <TableCell
                        className="border-none pt-0 pd-0"
                        width={180}
                        align="center"
                    >
                        <h3>Status</h3>
                    </TableCell>
                    <TableCell className="border-none pt-0 pd-0" align="right">
                        <h3>Passed</h3>
                    </TableCell>
                    <TableCell className="border-none pt-0 pd-0" align="right">
                        <h3>Failed</h3>
                    </TableCell>
                    <TableCell
                        width={200}
                        className="border-none pt-0 pd-0"
                        align="right"
                    >
                        <h3>Completed At</h3>
                    </TableCell>
                    <TableCell className="border-none pt-0 pd-0" align="right">
                        <h3>Detailed Status</h3>
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell className="pt-0" align="right"></TableCell>
                    <TableCell className="pt-0"></TableCell>
                    <TableCell className="pt-0" align="right">
                        <FormControl component="fieldset">
                            <FormGroup>
                                {INGESTION_STATUS.map((status: any) => {
                                    return (
                                        <FormControlLabel
                                            style={{ height: "30px" }}
                                            control={
                                                <Checkbox
                                                    checked={filterOptions.status.includes(
                                                        status.key
                                                    )}
                                                    onChange={(e: any) =>
                                                        onFilterOptionsChange({
                                                            status: e.target
                                                                .value,
                                                        })
                                                    }
                                                    name={status.label}
                                                    value={status.key}
                                                />
                                            }
                                            label={status.label}
                                        />
                                    );
                                })}
                            </FormGroup>
                        </FormControl>
                    </TableCell>
                    <TableCell className="pt-0" align="right"></TableCell>
                    <TableCell className="pt-0" align="right"></TableCell>
                    <TableCell className="pt-0" align="right"></TableCell>
                </TableRow>
            </>
        );
    };

    const handleChangePage = async (event: unknown, newPage: number) => {
        let skipKeyValue = "";
        let queue;
        if (newPage < page) {
            queue = popTwice();
            setskipKeyQueue([...queue]);
            skipKeyValue = queue[queue.length - 1] || "";
            console.log(skipKeyQueue);
        } else {
            skipKeyValue = skipKeyQueue[skipKeyQueue.length - 1];
        }
        console.log("newPage", newPage);
        setPage(newPage);
        const pages = await getIngestionLogs(rowsPerPage, skipKeyValue, queue);
        setIngestionLogsList([...pages.list]);
        setTotalCount(pages.totalCount);
    };

    const handleChangeRowsPerPage = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        setskipKeyQueue([]);
        const pages = await getIngestionLogs(event.target.value);
        setIngestionLogsList([...pages.list]);
        setTotalCount(pages.totalCount);
    };

    const pushKey = (key: any, queue: any = skipKeyQueue) => {
        const skipValue = [...queue, key];
        setskipKeyQueue(skipValue);
    };

    const popKey = (queue: any[] = skipKeyQueue) => {
        const skipValue = [...queue];
        const popValue = skipValue.pop();
        setskipKeyQueue(skipValue);
        return skipValue;
    };

    const popTwice = () => {
        const queue1 = popKey();
        const queue = popKey(queue1);
        return queue;
    };

    const ingestionValidatePopupClose = () => {
        setIsValidatePopupOpen(false);
    };

    const errorMessagFormattingForCSV = (errors: any) => {
        let errorsMessages: any[] = [];
        for (const err of errors.validation_error) {
            errorsMessages.push({
                rowNo: err.rowNo,
                errorMessage: err.errorMessage.join("\n"),
            });
        }
        return errorsMessages;
    };

    return (
        <>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>{tableRowRender()}</TableHead>
                    <TableBody>
                        {ingestionList &&
                            ingestionList.map((row: any, index: number) => (
                                <TableRow
                                    key={row.profileName}
                                    sx={{
                                        "&:last-child td, &:last-child th": {
                                            border: 0,
                                        },
                                    }}
                                >
                                    <TableCell align="right">
                                        {index + 1}
                                    </TableCell>
                                    <TableCell
                                        className="cursor-pointer"
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.studioName || ""}/
                                        {row.ingestionName}
                                    </TableCell>
                                    <TableCell align="right">
                                        {row?.ingestionStatus}
                                    </TableCell>
                                    <TableCell
                                        className="text-success"
                                        align="right"
                                    >
                                        {row?.passed || "-"}
                                    </TableCell>
                                    <TableCell
                                        className="text-danger"
                                        align="right"
                                    >
                                        {row?.failed || "-"}
                                    </TableCell>
                                    <TableCell align="right">
                                        {row?.ingestionStatus == "COMPLETED" ||
                                        row?.ingestionStatus == "FAILED"
                                            ? MILLIS_TO_LOCALE(row.lastModified)
                                            : "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                        <span
                                            className={
                                                row?.ingestionStatus ===
                                                    "COMPLETED" ||
                                                row?.ingestionStatus ===
                                                    "FAILED"
                                                    ? "cursor-pointer text-blue-600"
                                                    : "cursor-not-allowed text-gray-400"
                                            }
                                            onClick={
                                                row?.ingestionStatus ===
                                                    "COMPLETED" ||
                                                row?.ingestionStatus ===
                                                    "FAILED"
                                                    ? () =>
                                                          ingestionValidatePopupOpen(
                                                              { errors: row }
                                                          )
                                                    : undefined
                                            }
                                        >
                                            <VisibilityIcon />
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            {validationErrors && (
                <GeneralInfoModal
                    title={
                        <div className="d-flex j-space w-95">
                            Validation Errors
                            <CSVLink
                                headers={ERROR_HEADERS_CSV}
                                filename={selectedFile}
                                data={errorMessagFormattingForCSV(
                                    validationErrors.errors
                                )}
                                separator={";"}
                            >
                                <span className="download-btn">Download</span>
                            </CSVLink>
                        </div>
                    }
                    isOpen={isValidatePopupOpen}
                    handleClose={ingestionValidatePopupClose}
                >
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell width={100}>Row no</TableCell>
                                <TableCell align="left" width={300}>
                                    Error Message
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {validationErrors?.errors.validation_error.map(
                                (row: any) => (
                                    <TableRow
                                        key={row.rowNo}
                                        sx={{
                                            "&:last-child td, &:last-child th":
                                                { border: 0 },
                                        }}
                                    >
                                        <TableCell
                                            width={100}
                                            component="th"
                                            scope="row"
                                        >
                                            {row.rowNo}
                                        </TableCell>
                                        <TableCell width={300} align="left">
                                            <ul>
                                                {row?.errorMessage &&
                                                    row?.errorMessage.map(
                                                        (
                                                            errorMessage: string,
                                                            index: number
                                                        ) => (
                                                            <li key={index}>
                                                                {errorMessage}
                                                            </li>
                                                        )
                                                    )}
                                            </ul>
                                        </TableCell>
                                    </TableRow>
                                )
                            )}
                        </TableBody>
                    </Table>
                </GeneralInfoModal>
            )}
        </>
    );
});

export default ValidationLogsTable;
